import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { EstadoTag, dataBR, dataHoraBR } from "@/components/colab/colabUi";
import { avisarNovidades } from "@/hooks/useColabNovidades";

// Lote 162 — conversa e propostas de alteração de um processo compartilhado ("acompanhar").
// Serve o DONO (base "/colab/compartilhamentos/:id"), a conta parceira ("/colab/recebidos/:id") e o convidado ("/externo/compartilhamentos/:id").
// Regra de ouro: o parceiro NUNCA altera o dado — ele propõe; só o dono decide (e a nova data/hora é aplicada pelo caminho normal de edição).
const msgErro = (e, padrao) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : padrao);
const TOM_PROP = { pendente: "aguardando_aceite", aceita: "ativo" };
const quando = (de) => (de ? `${dataBR(de.data)}${de.hora ? ` às ${de.hora}` : ""}` : "—");

function Proposta({ p, lado, podeDecidir, onAcao, busy }) {
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const pendente = p.estado === "pendente";
  return (
    <li data-testid={`inter-prop-${p.proposta_id}`} className="rounded-md border border-stone-200 bg-white p-2.5 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-stone-800">{p.tipo_rotulo}</span>
        <EstadoTag estado={TOM_PROP[p.estado] || "outro"} rotulo={p.estado_rotulo} testid={`inter-prop-estado-${p.proposta_id}`} />
        <span className="text-[11px] text-stone-500 tabular-nums">{dataHoraBR(p.criada_em)}{lado === "dono" && p.autor_nome ? ` · ${p.autor_nome}` : ""}</span>
      </div>
      {p.tipo === "data_hora" ? (
        <div className="text-sm text-stone-900">
          <b>{p.titulo}</b>: <span className="tabular-nums text-stone-600 line-through">{quando(p.de)}</span> → <span className="tabular-nums font-semibold">{quando(p.para)}</span>
        </div>
      ) : (
        <p className="text-sm text-stone-900 whitespace-pre-wrap break-words">{p.texto}</p>
      )}
      {p.justificativa && <p className="text-xs text-stone-600 whitespace-pre-wrap break-words">Motivo: {p.justificativa}</p>}
      {p.estado === "aceita" && p.aplicada && <p className="text-xs text-emerald-900" data-testid={`inter-prop-aplicada-${p.proposta_id}`}>Aplicado no evento: {quando(p.aplicada)}.</p>}
      {p.estado === "aceita" && p.tipo === "texto" && <p className="text-xs text-stone-600">O remetente deu ciente. Nenhum dado foi alterado automaticamente.</p>}
      {p.estado === "recusada" && p.motivo_recusa && <p className="text-xs text-stone-600" data-testid={`inter-prop-motivo-${p.proposta_id}`}>Resposta: {p.motivo_recusa}</p>}
      {pendente && podeDecidir && (
        <div className="space-y-1.5">
          {!recusando ? (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" data-testid={`inter-aceitar-${p.proposta_id}`} disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white"
                onClick={() => onAcao(`propostas/${p.proposta_id}/decidir`, { aceitar: true }, p.tipo === "data_hora" ? "Proposta aceita — o evento foi remarcado" : "Proposta aceita (ciente)")}>
                {p.tipo === "data_hora" ? "Aceitar e remarcar" : "Dar ciente"}
              </Button>
              <Button size="sm" variant="outline" data-testid={`inter-recusar-${p.proposta_id}`} disabled={busy} onClick={() => setRecusando(true)}>Recusar</Button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Input data-testid={`inter-recusar-motivo-${p.proposta_id}`} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} placeholder="Motivo (opcional)" />
              <div className="flex gap-2">
                <Button size="sm" data-testid={`inter-recusar-ok-${p.proposta_id}`} disabled={busy} className="bg-rose-700 hover:bg-rose-800 text-white"
                  onClick={() => onAcao(`propostas/${p.proposta_id}/decidir`, { aceitar: false, motivo }, "Proposta recusada")}>Confirmar recusa</Button>
                <Button size="sm" variant="ghost" onClick={() => setRecusando(false)}>Voltar</Button>
              </div>
            </div>
          )}
          {p.tipo === "data_hora" && !recusando && <p className="text-[11px] text-stone-500">Ao aceitar, a data e a hora do evento mudam na sua agenda (e no Google Agenda, se estiver ligado).</p>}
        </div>
      )}
      {pendente && lado === "parceiro" && (
        <Button size="sm" variant="ghost" data-testid={`inter-cancelar-${p.proposta_id}`} disabled={busy} className="text-stone-600"
          onClick={() => onAcao(`propostas/${p.proposta_id}/cancelar`, undefined, "Proposta cancelada")}>Cancelar proposta</Button>
      )}
    </li>
  );
}

function FormProposta({ sid, eventos, busy, onEnviar }) {
  const [tipo, setTipo] = useState("data_hora");
  const [ref, setRef] = useState("");
  const [data, setData] = useState("");
  const [hora, setHora] = useState("");
  const [texto, setTexto] = useState("");
  const [just, setJust] = useState("");
  const [erro, setErro] = useState("");
  const enviar = async () => {
    setErro("");
    if (tipo === "data_hora" && (!ref || !data)) return setErro("Escolha o evento e a nova data.");
    if (tipo === "texto" && texto.trim().length < 3) return setErro("Descreva a correção ou sugestão.");
    const ok = await onEnviar(tipo === "data_hora" ? { tipo, ref, data, hora: hora || undefined, justificativa: just || undefined } : { tipo, texto, justificativa: just || undefined });
    if (ok) { setRef(""); setData(""); setHora(""); setTexto(""); setJust(""); }
  };
  const cls = "h-9 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-800";
  return (
    <div data-testid={`inter-form-${sid}`} className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-2">
      <div className="text-xs font-semibold text-stone-700">Propor uma alteração</div>
      <select data-testid={`inter-propor-tipo-${sid}`} aria-label="Tipo de proposta" value={tipo} onChange={(e) => setTipo(e.target.value)} className={`${cls} w-full`}>
        <option value="data_hora">Nova data/hora de um evento</option>
        <option value="texto">Correção ou sugestão (texto)</option>
      </select>
      {tipo === "data_hora" ? (
        <div className="space-y-2">
          <select data-testid={`inter-propor-evento-${sid}`} aria-label="Evento" value={ref} onChange={(e) => setRef(e.target.value)} className={`${cls} w-full`}>
            <option value="">{eventos.length ? "Escolha o evento…" : "Nenhum evento disponível para remarcar"}</option>
            {eventos.map((e) => <option key={e.ref} value={e.ref}>{e.titulo} — {dataBR(e.data)}{e.hora ? ` ${e.hora}` : ""}</option>)}
          </select>
          <div className="flex gap-2 flex-wrap">
            <label className="text-xs text-stone-600 flex flex-col gap-0.5">Nova data
              <input type="date" data-testid={`inter-propor-data-${sid}`} value={data} onChange={(e) => setData(e.target.value)} className={cls} />
            </label>
            <label className="text-xs text-stone-600 flex flex-col gap-0.5">Nova hora (opcional)
              <input type="time" data-testid={`inter-propor-hora-${sid}`} value={hora} onChange={(e) => setHora(e.target.value)} className={cls} />
            </label>
          </div>
        </div>
      ) : (
        <Textarea data-testid={`inter-propor-texto-${sid}`} rows={3} maxLength={1000} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Ex.: o nome da parte ré está incompleto" />
      )}
      <Input data-testid={`inter-propor-just-${sid}`} value={just} onChange={(e) => setJust(e.target.value)} maxLength={500} placeholder="Motivo (opcional)" />
      {erro && <p role="alert" data-testid={`inter-propor-erro-${sid}`} className="text-xs text-rose-800">{erro}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" data-testid={`inter-propor-enviar-${sid}`} disabled={busy} onClick={enviar} className="bg-stone-900 hover:bg-stone-800 text-white">Enviar proposta</Button>
        <span className="text-[11px] text-stone-500">Nada muda sozinho: o remetente é avisado e decide.</span>
      </div>
    </div>
  );
}

export default function ColabInteracao({ sid, base, lado, eventos = [], remetente, onMudou }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(false);
  const [texto, setTexto] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setD((await http.get(`${base}/interacao`)).data); setErro(false); avisarNovidades(); } catch { setErro(true); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const acao = async (rel, corpo, ok) => {
    setBusy(true);
    try { await http.post(`${base}/${rel}`, corpo); toast.success(ok); await load(); onMudou?.(); return true; }
    catch (e) { toast.error(msgErro(e, "Não foi possível concluir")); await load(); return false; } finally { setBusy(false); }
  };
  const comentar = async () => {
    if (!texto.trim()) return;
    setBusy(true);
    try { await http.post(`${base}/comentarios`, { texto }); setTexto(""); toast.success("Comentário enviado"); await load(); }
    catch (e) { toast.error(msgErro(e, "Não foi possível enviar o comentário")); } finally { setBusy(false); }
  };
  const propor = async (corpo) => {
    setBusy(true);
    try { await http.post(`${base}/propostas`, corpo); toast.success("Proposta enviada"); await load(); return true; }
    catch (e) { toast.error(msgErro(e, "Não foi possível enviar a proposta")); return false; } finally { setBusy(false); }
  };

  if (erro) return <div data-testid={`inter-erro-${sid}`} className="text-sm text-rose-800">Não foi possível carregar a conversa. <button type="button" className="underline font-semibold" onClick={load}>Tentar de novo</button></div>;
  if (!d) return <div className="text-sm text-stone-500">Carregando conversa…</div>;
  const eventosPropostos = eventos.filter((e) => !e.cancelado && !e.removido && !e.indisponivel);
  return (
    <div data-testid={`inter-${sid}`} className="space-y-3">
      <section>
        <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-1">Conversa</div>
        {d.comentarios.length === 0 ? <p data-testid={`inter-vazio-${sid}`} className="text-xs text-stone-500 italic">Nenhum comentário ainda.</p> : (
          <ul data-testid={`inter-comentarios-${sid}`} className="space-y-1.5">
            {d.comentarios.map((c) => (
              <li key={c.comentario_id} data-testid={`inter-com-${c.comentario_id}`} className={`rounded-md border px-2.5 py-1.5 text-sm ${c.meu ? "bg-stone-50 border-stone-200" : "bg-white border-stone-200"}`}>
                <div className="text-[11px] text-stone-500"><b className="text-stone-700">{c.meu ? "Você" : (c.autor_tipo === "dono" ? (remetente || c.autor_nome) : c.autor_nome) || "—"}</b> · <span className="tabular-nums">{dataHoraBR(c.criado_em)}</span></div>
                <div className="whitespace-pre-wrap break-words text-stone-900">{c.texto}</div>
              </li>
            ))}
          </ul>
        )}
        {d.pode_comentar ? (
          <div className="mt-2 space-y-1.5">
            <Textarea data-testid={`inter-comentario-texto-${sid}`} rows={2} maxLength={d.limites?.comentario || 1000} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Escreva um comentário" />
            <div className="flex items-center gap-2">
              <Button size="sm" data-testid={`inter-comentario-enviar-${sid}`} disabled={busy || !texto.trim()} onClick={comentar} className="bg-stone-900 hover:bg-stone-800 text-white">Enviar comentário</Button>
              <span className="text-[11px] text-stone-500 tabular-nums">{texto.length}/{d.limites?.comentario || 1000}</span>
            </div>
          </div>
        ) : !d.acesso_ativo ? <p className="text-[11px] text-stone-500 mt-1" data-testid={`inter-leitura-${sid}`}>O acesso não está ativo: a conversa fica só para leitura.</p> : null}
      </section>

      <section>
        <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-1">Propostas de alteração</div>
        {d.propostas.length === 0 ? <p data-testid={`inter-sem-propostas-${sid}`} className="text-xs text-stone-500 italic">Nenhuma proposta.</p> : (
          <ul data-testid={`inter-propostas-${sid}`} className="space-y-1.5">
            {d.propostas.map((p) => <Proposta key={p.proposta_id} p={p} lado={lado} podeDecidir={d.pode_decidir} busy={busy} onAcao={acao} />)}
          </ul>
        )}
        {d.pode_propor && <div className="mt-2"><FormProposta sid={sid} eventos={eventosPropostos} busy={busy} onEnviar={propor} /></div>}
      </section>
    </div>
  );
}
